// Dependency-free HTML sanitizer for sponsor-authored rich text (B7). Uses the browser's
// native DOMParser — no external package. Allowlist approach: keep a small set of formatting
// tags + safe attributes; drop scripts/styles/iframes wholesale; unwrap any other tag (keeping
// its text); strip event-handler attributes and javascript:/data: URLs.
//
// The sponsor bio is editable from the sponsor portal, so it must be scrubbed before it reaches
// a participant's browser.

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'span',
  'div',
  'hr',
  'sub',
  'sup'
]);

// Removed together with their contents (not just unwrapped).
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'svg',
  'math'
]);

const ALLOWED_ATTRS = { a: ['href', 'target', 'rel'] };

function isSafeHref(href) {
  return !/^\s*(javascript|data|vbscript):/i.test(href || '');
}

export function sanitizeHtml(dirty) {
  if (!dirty) return '';
  // No DOM available (SSR/tests) — render nothing rather than unsanitized markup.
  if (typeof window === 'undefined' || !window.DOMParser) return '';

  const doc = new DOMParser().parseFromString(String(dirty), 'text/html');

  const clean = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      // Text nodes (3) are safe; keep them.
      if (child.nodeType === 3) return;
      // Anything that isn't an element (1) — comments, etc. — is dropped.
      if (child.nodeType !== 1) {
        child.remove();
        return;
      }

      const tag = child.tagName.toLowerCase();

      if (DROP_WITH_CONTENT.has(tag)) {
        child.remove();
        return;
      }

      // Recurse first so unwrapping preserves already-cleaned descendants.
      clean(child);

      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap unknown-but-harmless tags: replace with their (cleaned) children.
        child.replaceWith(...Array.from(child.childNodes));
        return;
      }

      const allowed = ALLOWED_ATTRS[tag] || [];
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (!allowed.includes(name) || name.startsWith('on')) {
          child.removeAttribute(attr.name);
        }
      });

      if (tag === 'a') {
        if (!isSafeHref(child.getAttribute('href'))) {
          child.removeAttribute('href');
        }
        if (child.getAttribute('target') === '_blank') {
          child.setAttribute('rel', 'noopener noreferrer');
        }
      }
    });
  };

  clean(doc.body);
  return doc.body.innerHTML;
}

// Plain-text extraction for previews/excerpts (e.g. a line-clamped sponsor description on a
// card) where rich markup can't be rendered. Strips all tags and collapses whitespace, so
// sponsor-authored HTML never leaks as literal "<p>" text.
export function stripHtml(dirty) {
  if (!dirty) return '';
  if (typeof window === 'undefined' || !window.DOMParser) return '';

  const doc = new DOMParser().parseFromString(String(dirty), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

export default sanitizeHtml;
